import React, { useCallback, useMemo } from 'react';
import { ColumnsType } from 'antd/es/table';
import { Column, DataTable, Label, Row } from '@ydistri/ds';
import { styled } from 'styled-components';
import { COLORS, computeRemSize } from '@ydistri/theme';
import {
  EntityListSkuResponse,
  SkuClassResponse,
  SkuDescriptionResponse,
  TargetListSkuResponse as RequestListSkuResponse,
} from '@ydistri/api-sdk';
import NoData, {
  NO_DATA_PLACEHOLDER,
} from '../../../../../../../../components/global/NoData/NoData';
import SkuAttributesTable, { SkuAttribute } from './SkuAttributesTable';
import SkuDescription from '../../../../../../../../components/global/SkuDescription/SkuDescription';
import ActualDataWrapper from './ActualDataWrapper';
import DataDeletedEntityListIcon from '../../../../../../../../components/domain/EntityLists/DataDeletedEntityListIcon';
import ArchivedEntityListIcon from '../../../../../../../../components/domain/EntityLists/ArchivedEntityListIcon';

interface SkuInfoTableWrapperProps {
  $showActualData?: boolean;
  $containsSkuInfo?: boolean;
}

const SkuInfoTableWrapper = styled.div<SkuInfoTableWrapperProps>`
  flex-grow: 1;
  min-height: ${props => (props.$containsSkuInfo ? '350px' : '165px')};

  padding: ${computeRemSize(8)};
  border: ${computeRemSize(1)} solid lightgrey;
  box-shadow: inset 0 ${computeRemSize(2)} ${computeRemSize(10)} #f5f5f5;
  background-color: ${COLORS.GREY_LIGHT};
  border-radius: 0.4em;

  td.ant-table-cell {
    vertical-align: top;
  }

  .ant-table-thead .ant-table-cell {
    background-color: transparent;
    font-size: 1.2rem;
    font-weight: 500;
  }

  .ant-table {
    background: transparent;
  }

  .ant-table-thead
    .ant-table-cell:not(:last-child):not(.ant-table-selection-column):not(
      .ant-table-row-expand-icon-cell
    ):not([colspan])::before {
    width: 0;
  }

  #SkuInfoHeaderCell-value {
    ${props => props.$showActualData && `color: ${COLORS.PRIMARY};`}
  }
`;

export interface SkuInfoData {
  label: string;
  configuration: React.ReactNode;
  value?: React.ReactNode;
}

interface SkuInfoProps {
  data: SkuInfoData[];
  skuClass?: SkuClassResponse;
  requestList?: RequestListSkuResponse;
  skuDescription?: SkuDescriptionResponse;
  showActualData?: boolean;
  minLayerLists?: EntityListSkuResponse[];
}

/**
 * Display SKU Information including SKU class, request list, and other attributes in a table format.
 *
 * If the showActualData flag is true, the values in the "Value" column MUST be displayed in the primary color to highlight that they represent actual data rather than placeholders.
 * @param data
 * @param skuClass
 * @param requestList
 * @param skuDescription
 * @param showActualData - if true, display the values using PRIMARY color
 * @param minLayerLists
 * @constructor
 */
const SkuInfo: React.FC<SkuInfoProps> = ({
  data,
  skuClass,
  requestList,
  skuDescription,
  showActualData,
  minLayerLists,
}) => {
  const rowKeyProvider = useCallback((record: SkuInfoData) => record.label, []);

  const columnsAttributes: ColumnsType<SkuInfoData> = useMemo(
    () => [
      {
        dataIndex: 'label',
        key: 'label',
        title: 'Settings',
        width: '18rem',
        render: (value: string) => <Label>{value}</Label>,
      },
      {
        dataIndex: 'configuration',
        key: 'configuration',
        title: 'Configuration',
        width: '11rem',
      },
      {
        dataIndex: 'value',
        key: 'value',
        title: 'Value',
        render: (value: React.ReactNode) =>
          showActualData ? <ActualDataWrapper>{value}</ActualDataWrapper> : value,
        onHeaderCell: () => ({
          id: 'SkuInfoHeaderCell-value',
        }),
      },
    ],
    [showActualData],
  );

  const attributes = useMemo(() => {
    const result: SkuAttribute[] = [
      {
        label: 'SKU class',
        value: skuClass?.name ?? <NoData />,
        secondaryValue: skuClass?.customerSkuClassId?.toString() ?? <NoData />,
      },
    ];

    if (requestList) {
      result.push({
        label: 'Request List',
        value:
          requestList.isDataDeleted || requestList.isArchived ? (
            <Row $gap="0.5rem" $alignItems="center">
              {requestList.name ?? <NoData />}
              {requestList.isDataDeleted ? (
                <DataDeletedEntityListIcon />
              ) : (
                <ArchivedEntityListIcon />
              )}
            </Row>
          ) : (
            (requestList.name ?? <NoData />)
          ),
        secondaryValue: `${requestList.quantity ?? NO_DATA_PLACEHOLDER} MU`,
      });
    }

    return result;
  }, [skuClass?.name, skuClass?.customerSkuClassId, requestList]);

  const columnsMinLayers: ColumnsType<EntityListSkuResponse> = useMemo(
    () => [
      {
        dataIndex: 'name',
        key: 'name',
        title: 'Name',
        width: '20rem',
        render: (value: string | null, record: EntityListSkuResponse) => (
          <Row $gap="0.5rem" $alignItems="center">
            {value}
            {record.isDataDeleted ? (
              <DataDeletedEntityListIcon of="version" />
            ) : (
              record.isArchived && <ArchivedEntityListIcon />
            )}
          </Row>
        ),
      },
      {
        dataIndex: 'quantity',
        key: 'quantity',
        title: 'Quantity',
        width: '5rem',
        render: value => `${value} MU`,
      },
    ],
    [],
  );

  //display min layer lists sorted by name for natural readability
  const dispMinLayers = useMemo(
    () => [...(minLayerLists ?? [])].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')),
    [minLayerLists],
  );

  const containsSkuInfo = data.length > 0;

  return (
    <SkuInfoTableWrapper
      $showActualData={showActualData}
      $containsSkuInfo={containsSkuInfo}
      data-type="SkuInfoWrapper"
    >
      <Column $gap="1rem">
        <SkuDescription skuDescription={skuDescription} showActualData={showActualData} />
        <SkuAttributesTable attributes={attributes} showActualData={showActualData} />
        {containsSkuInfo && (
          <DataTable
            id="SkuInfo"
            dataSource={data}
            columns={columnsAttributes}
            showHeader={true}
            rowKey={rowKeyProvider}
          />
        )}
      </Column>
      {minLayerLists && minLayerLists.length > 0 && (
        <Column $gap="0.5rem" $padding={0.25}>
          <Label>Minimum layer lists</Label>
          <DataTable
            id="SkuMinLayers"
            dataSource={dispMinLayers}
            columns={columnsMinLayers}
            flexGrow={0}
            showBorder={true}
            showHeader={false}
          />
        </Column>
      )}
    </SkuInfoTableWrapper>
  );
};

export default SkuInfo;
